import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { db } from '../firebase';
import Swal from 'sweetalert2';
import { collection, addDoc, query, where, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { Send, Info, Calendar as CalendarIcon } from 'lucide-react';

const Booking = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [formData, setFormData] = useState({
    nama: '',
    whatsapp: '',
    checkIn: '',
    checkOut: '',
    guests: '10-15'
  });

  useEffect(() => {
    const q = query(collection(db, "reservasi"), where("status", "==", "confirmed"));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dates = [];
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        let start = new Date(data.checkIn);
        let end = new Date(data.checkOut);
        
        let current = new Date(start);
        while (current <= end) {
          dates.push(current.toDateString());
          current.setDate(current.getDate() + 1);
        }
      });
      setBookedDates(dates);
    });

    return () => unsubscribe();
  }, []);

  const isBooked = ({ date, view }) => {
    if (view === 'month') {
      return bookedDates.includes(date.toDateString());
    }
  };

  const checkDateConflict = (checkIn, checkOut) => {
    let start = new Date(checkIn);
    let end = new Date(checkOut);
    let current = new Date(start);
    
    while (current <= end) {
      if (bookedDates.includes(current.toDateString())) {
        return true;
      }
      current.setDate(current.getDate() + 1);
    }
    return false; // Aman
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 1. Validasi Logika Tanggal
    if (new Date(formData.checkOut) <= new Date(formData.checkIn)) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Tanggal Check-Out harus setelah tanggal Check-In!',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    // 2. Validasi Bentrok Tanggal
    const isConflict = checkDateConflict(formData.checkIn, formData.checkOut);
    if (isConflict) {
      Swal.fire({
        icon: 'warning',
        title: 'Tanggal Tidak Tersedia',
        text: 'Maaf, salah satu tanggal yang Anda pilih sudah terbooking.',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    // 3. Konfirmasi Sebelum Kirim
    const confirm = await Swal.fire({
      title: 'Konfirmasi Reservasi?',
      text: "Data Anda akan dikirim ke admin untuk pengecekan.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#4b5563',
      confirmButtonText: 'Ya, Kirim Sekarang!',
      cancelButtonText: 'Batal'
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);

    try {
      // Simpan ke Firebase
      await addDoc(collection(db, "reservasi"), {
        ...formData,
        status: 'pending',
        timestamp: serverTimestamp()
      });

      // FORMAT PESAN WHATSAPP
      const message = `Halo Admin Difa BallaEja, saya ingin reservasi:%0A%0A` +
                      `*Nama:* ${formData.nama}%0A` +
                      `*WhatsApp:* ${formData.whatsapp}%0A` +
                      `*Check-In:* ${formData.checkIn}%0A` +
                      `*Check-Out:* ${formData.checkOut}%0A` +
                      `*Jumlah Tamu:* ${formData.guests} orang`;

      const waUrl = `https://wa.me/6282196694854?text=${message}`;

      // SOLUSI UTAMA: Gunakan window.location.href agar langsung redirect di tab yang sama
      // Ini jauh lebih aman dari blokir pop-up browser daripada window.open
      setLoading(false);
      window.location.href = waUrl;

    } catch (error) {
      console.error("Error adding document: ", error);
      Swal.fire({
        icon: 'error',
        title: 'Gagal Terkirim',
        text: 'Terjadi masalah koneksi. Silakan coba lagi.',
        confirmButtonColor: '#dc2626',
      });
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen font-poppins py-8 sm:py-12 lg:py-16 px-4 sm:px-8 lg:px-24">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Section - Ukuran teks responsif */}
        <div className="text-center mb-10 lg:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-4">
            Booking & <span className="text-red-600">Ketersediaan</span>
          </h1>
          <div className="w-16 sm:w-24 h-1 bg-red-600 mx-auto"></div>
        </div>

        {/* Grid System - 1 kolom di mobile, 2 kolom di desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          
          {/* KOLOM KIRI: Kalender & Info */}
          <div className="space-y-6 lg:space-y-8 order-1 lg:order-1">
            <div className="bg-gray-50 p-4 sm:p-6 lg:p-8 border-t-4 border-red-600 shadow-sm rounded-sm">
              <div className="flex items-center space-x-3 mb-6">
                <CalendarIcon className="text-red-600 w-5 h-5 sm:w-6 sm:h-6" />
                <h2 className="text-lg sm:text-xl font-bold uppercase tracking-widest text-gray-800">Cek Tanggal</h2>
              </div>
              
              {/* Kalender dengan padding responsif */}
              <div className="custom-calendar overflow-hidden rounded-lg bg-white p-2 sm:p-4">
                <Calendar 
                  onChange={setSelectedDate} 
                  value={selectedDate}
                  tileDisabled={({ date }) => bookedDates.includes(date.toDateString())}
                  tileClassName={({ date, view }) => 
                    isBooked({ date, view }) ? 'booked-tile' : null
                  }
                  className="w-full border-none shadow-none text-sm sm:text-base"
                />
              </div>

              {/* Legenda - Flex-wrap agar tidak terpotong di layar kecil */}
              <div className="mt-6 flex flex-wrap gap-4 text-[10px] sm:text-xs font-bold uppercase tracking-tighter">
                {/* <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#5b5b5b' }}></div>
                    <span className="text-gray-600">Hari ini</span>
                </div> */}
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                  <span>Terbooking</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                  <span>Tersedia</span>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 bg-red-50 border-l-4 border-red-600 text-red-800 text-xs sm:text-sm italic">
              <div className="flex items-start">
                <Info size={18} className="mr-3 mt-1 flex-shrink-0" />
                <p>Pilih tanggal di kalender. Tanggal yang berwarna merah sudah dikonfirmasi oleh admin dan tidak dapat dipesan.</p>
              </div>
            </div>
          </div>

          {/* KOLOM KANAN: Form - Padding disesuaikan */}
          <div className="bg-white p-6 sm:p-8 lg:p-10 border border-gray-100 shadow-2xl rounded-sm order-1 lg:order-2">
            <h2 className="text-xl sm:text-2xl font-serif font-bold mb-6 lg:mb-8 border-b pb-4 text-gray-800">Formulir Reservasi</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 lg:mb-2">Nama Lengkap</label>
                <input 
                  type="text" required
                  placeholder="Contoh: Budi Santoso"
                  className="w-full p-3 sm:p-4 bg-gray-50 border-b-2 border-transparent focus:border-red-600 outline-none transition-all text-sm"
                  onChange={(e) => setFormData({...formData, nama: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 lg:mb-2">Nomor WhatsApp</label>
                <input 
                  type="tel" required
                  placeholder="0812xxxx"
                  className="w-full p-3 sm:p-4 bg-gray-50 border-b-2 border-transparent focus:border-red-600 outline-none transition-all text-sm"
                  onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                />
              </div>

              {/* Grid Input Tanggal - 1 kolom di HP kecil, 2 kolom di Tablet ke atas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 lg:mb-2">Check-In</label>
                  <input 
                    type="date" required
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full p-3 sm:p-4 bg-gray-50 border-b-2 border-transparent focus:border-red-600 outline-none transition-all text-sm"
                    onChange={(e) => setFormData({...formData, checkIn: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 lg:mb-2">Check-Out</label>
                  <input 
                    type="date" required
                    min={formData.checkIn || new Date().toISOString().split("T")[0]}
                    className="w-full p-3 sm:p-4 bg-gray-50 border-b-2 border-transparent focus:border-red-600 outline-none transition-all text-sm"
                    onChange={(e) => setFormData({...formData, checkOut: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 lg:mb-2">Jumlah Tamu</label>
                <select 
                  className="w-full p-3 sm:p-4 bg-gray-50 border-b-2 border-transparent focus:border-red-600 outline-none cursor-pointer text-gray-600 text-sm"
                  onChange={(e) => setFormData({...formData, guests: e.target.value})}
                  value={formData.guests}
                >
                  <option value="10-15">10 - 15 Orang</option>
                  <option value="15-20">15 - 20 Orang</option>
                  <option value="20-25">20 - 25 Orang</option>
                </select>
              </div>

              <button 
                type="submit"
                disabled={loading}
                className={`w-full py-4 lg:py-5 font-bold tracking-[0.2em] sm:tracking-[0.3em] transition-all duration-500 shadow-xl flex items-center justify-center space-x-4
                  ${loading ? 'bg-gray-400' : 'bg-red-600 hover:bg-black text-white text-xs sm:text-base'}`}
              >
                <span>{loading ? 'MEMPROSES...' : 'KIRIM DATA RESERVASI'}</span>
                <Send size={18} className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </form>
          </div>

        </div>
      </div>

      <style>{`
        /* Container Kalender */
        .react-calendar { 
            border: none !important; 
            width: 100% !important; 
            font-family: 'Poppins', sans-serif; 
        }

        /* Responsive Tile */
        .react-calendar__tile {
            padding: 8px 4px !important;
            margin: 2px 0 !important;
            height: auto !important;
            aspect-ratio: 1 / 1; /* Biar tetap kotak di HP */
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            color: #374151 !important;
            font-size: 0.75rem; /* Lebih kecil di HP */
        }

        /* Tile lebih besar di layar Tablet/Desktop */
        @media (min-width: 640px) {
            .react-calendar__tile {
                padding: 12px 8px !important;
                margin: 4px 0 !important;
                font-size: 1rem;
                height: 50px !important;
                aspect-ratio: auto;
            }
        }

        .booked-tile { 
            background: #dc2626 !important; 
            color: #ffffff !important; 
            border-radius: 4px sm:8px !important;
            cursor: not-allowed !important;
        }

        .react-calendar__tile--now { 
            background: #FFD300 !important; 
            color: #000000 !important; 
            border-radius: 4px sm:8px !important;
            font-weight: bold !important;
        } 

        .react-calendar__tile:enabled:hover {
            background-color: #f3f4f6 !important;
            color: #000000 !important;
            border-radius: 4px sm:8px !important;
        }

        .react-calendar__tile--active,
        .react-calendar__tile--active:enabled:hover,
        .react-calendar__tile--active:enabled:focus { 
            background: #4b5563 !important; 
            color: #ffffff !important; 
            border-radius: 4px sm:8px !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .react-calendar__navigation {
            margin-bottom: 10px lg:20px !important;
        }
        
        .react-calendar__navigation button {
            font-weight: bold;
            text-transform: uppercase;
            color: #111827;
            font-size: 0.8rem sm:1rem;
        }
        `}</style>
    </div>
  );
};

export default Booking;